import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Loader2, Dog, MapPin, Heart, Star, Shield, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import { getImageUrl } from '@/utils/imageUrl';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

const BASE_URL = window.location.origin;

export default function StudsByCity() {
  const { t } = useTranslation();
  const { breed, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [breed, city]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/public/reproduktory-miasto/${breed}/${city}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  const { breed: breedName, city: cityName, studs, count, seo } = data || {};

  return (
    <>
      <Helmet>
        <title>{seo?.title || `Reproduktory ${breedName} ${cityName}`}</title>
        <meta name="description" content={seo?.description} />
        <link rel="canonical" href={`${BASE_URL}/reproduktory/${breed?.toLowerCase().replace(/\s+/g, '-')}/${city?.toLowerCase().replace(/\s+/g, '-')}`} />
        {seo?.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-900/90 backdrop-blur border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <span className="text-2xl">🐾</span>
              <span className="font-semibold text-xl text-white">ApisAI</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/logowanie">
                <Button variant="ghost" className="text-gray-300">Zaloguj</Button>
              </Link>
              <Link to="/rejestracja">
                <Button className="bg-orange-600 hover:bg-orange-700">Dołącz za darmo</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-24 pb-12 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              {seo?.breadcrumbs?.map((crumb, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="w-4 h-4" />}
                  <Link to={crumb.url} className="hover:text-white transition">
                    {crumb.name}
                  </Link>
                </span>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full text-amber-300 text-sm mb-4">
                <Heart className="w-4 h-4" />
                Reproduktory
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {seo?.h1 || `Reproduktory ${breedName} w ${cityName}`}
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Szukasz psa do krycia? Znajdź reproduktora {breedName} z badaniami zdrowotnymi i udokumentowanym rodowodem.
                {count > 0 ? ` ${count} dostępnych reproduktorów.` : ''}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            {studs?.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {studs.map((stud, i) => (
                  <StudCard key={stud.id || i} stud={stud} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-zinc-800/30 rounded-xl border border-zinc-700/50">
                <Dog className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Brak reproduktorów {breedName} w {cityName}
                </h3>
                <p className="text-gray-400 mb-6">
                  Nie znaleźliśmy jeszcze reproduktorów tej rasy w Twoim mieście.
                </p>
                <Link to="/rejestracja">
                  <Button className="bg-orange-600 hover:bg-orange-700">
                    Zarejestruj swojego reproduktora
                  </Button>
                </Link>
              </div>
            )}

            {/* CTA */}
            <div className="mt-16 text-center bg-gradient-to-r from-amber-600/20 to-orange-600/20 rounded-2xl p-8 border border-amber-500/20">
              <h3 className="text-2xl font-bold text-white mb-4">
                Masz reproduktora {breedName}?
              </h3>
              <p className="text-gray-300 mb-6 max-w-xl mx-auto">
                Dodaj swojego psa do ApisAI i docieraj do hodowców szukających reproduktorów 
                w {cityName} i okolicy.
              </p>
              <Link to="/rejestracja">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                  Dodaj reproduktora
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <ApisAIFooter />
      </div>
    </>
  );
}

function StudCard({ stud }) {
  const mainPhoto = getImageUrl(stud.main_photo);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/50 rounded-xl overflow-hidden border border-zinc-700 hover:border-amber-500/50 transition"
    >
      {/* Photo */}
      <div className="h-48 bg-zinc-900 relative">
        {mainPhoto ? (
          <img 
            src={mainPhoto} 
            alt={stud.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog className="w-12 h-12 text-zinc-700" />
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-2">
          {stud.titles_verified && (
            <span className="px-2 py-1 bg-emerald-500/90 text-white text-xs rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Zweryfikowany
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-1">
          {stud.name}
        </h3>
        <p className="text-gray-400 text-sm mb-3">{stud.breed}</p>
        
        <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span>{stud.city}{stud.voivodeship ? `, ${stud.voivodeship}` : ''}</span>
        </div>

        {/* Titles */}
        {stud.selected_championships?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {stud.selected_championships.slice(0, 3).map((title, i) => (
              <span key={i} className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full uppercase">
                {title.replace('ch_', 'CH ')}
              </span>
            ))}
          </div>
        )}

        {/* Health Tests */}
        {stud.health_tests && Object.keys(stud.health_tests).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {Object.entries(stud.health_tests).slice(0, 3).map(([test, result], i) => (
              <span key={i} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs rounded-full">
                {test.toUpperCase()}: {result}
              </span>
            ))}
          </div>
        )}

        {/* Price if available */}
        {stud.stud_price && (
          <p className="text-amber-400 font-medium text-sm mb-3">
            {stud.stud_price}
          </p>
        )}

        {/* Actions */}
        <Link to={`/reproduktor/${stud.slug}`}>
          <Button className="w-full bg-amber-600 hover:bg-amber-700">
            Zobacz profil
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </motion.div>
  );
}
